// RiskJourney — overlay component for UC-1 (per-risk journey view).
// Renders three labeled dots (I, R, T) with dashed arrows between them.
// Convention: cellCenter(likelihood, impact, cols, rows) where
//   cols = likelihood levels (X-axis), rows = impact levels (Y-axis).

import type { RiskJourneyData } from './types'
import { cellCenter, DOT_RADIUS } from './risk-grid-utils'

interface RiskJourneyProps {
  journey: RiskJourneyData
  cols: number
  rows: number
}

interface DotDef {
  key: string
  label: string
  colorVar: string
  position: { impact: number; likelihood: number } | null
}

export function RiskJourney({ journey, cols, rows }: RiskJourneyProps) {
  const dots: DotDef[] = [
    { key: 'inherent', label: 'I', colorVar: '--grid-node-inherent', position: journey.inherent },
    { key: 'residual', label: 'R', colorVar: '--grid-node-residual', position: journey.residual },
    { key: 'target', label: 'T', colorVar: '--grid-node-target', position: journey.target },
  ]

  // Compute centers for positioned dots
  const positioned = dots
    .filter((d) => d.position !== null)
    .map((d) => ({
      ...d,
      center: cellCenter(d.position!.likelihood, d.position!.impact, cols, rows),
    }))

  // Draw arrows between consecutive positioned dots
  const arrows: { x1: number; y1: number; x2: number; y2: number; key: string }[] = []
  for (let i = 0; i < positioned.length - 1; i++) {
    const from = positioned[i]!
    const to = positioned[i + 1]!

    // Skip arrow if dots are in the same cell
    if (from.center.x === to.center.x && from.center.y === to.center.y) continue

    // Shorten line to avoid overlapping dots
    const dx = to.center.x - from.center.x
    const dy = to.center.y - from.center.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    const clearance = DOT_RADIUS + 4

    if (dist <= clearance * 2) continue // too close

    const ux = dx / dist
    const uy = dy / dist

    arrows.push({
      x1: from.center.x + ux * clearance,
      y1: from.center.y + uy * clearance,
      x2: to.center.x - ux * clearance,
      y2: to.center.y - uy * clearance,
      key: `arrow-${from.key}-${to.key}`,
    })
  }

  return (
    <g>
      {/* Arrows (behind dots) */}
      {arrows.map((a) => (
        <line
          key={a.key}
          x1={a.x1}
          y1={a.y1}
          x2={a.x2}
          y2={a.y2}
          stroke="hsl(var(--muted-foreground))"
          strokeWidth={1.5}
          strokeDasharray="4 3"
          markerEnd="url(#grid-arrow)"
        />
      ))}

      {/* Dots with labels */}
      {positioned.map((d) => (
        <g key={d.key} aria-label={`${d.label}: ${journey.name}`}>
          <circle
            cx={d.center.x}
            cy={d.center.y}
            r={DOT_RADIUS}
            fill={`hsl(var(${d.colorVar}))`}
            stroke="hsl(var(--background))"
            strokeWidth={2}
          />
          <text
            x={d.center.x}
            y={d.center.y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={12}
            fontWeight="bold"
            fill="white"
          >
            {d.label}
          </text>
        </g>
      ))}
    </g>
  )
}
